import { createFileRoute } from "@tanstack/react-router"
import { useMemo } from "react"
import VariantTableCard, {
  VariantsTableRow,
} from "~/components/product/VariantTableCard/VariantTableCard"
import { useVariantContext } from "~/context/VariantProvider"
import { usePageTracking } from "~/hooks/usePageTracking"
import { useSkuStore } from "~/providers/sku-store-provider"
import { InventoryGraphCard } from "~/components/home/InventoryGraphCard"
import { VariantCollectionSalesCard } from "~/components/forecast/VariantCollectionSalesCard"
import { CustomerSalesCard } from "~/components/forecast/CustomerSalesCard"
import { api } from "~/utils/api"
import VariantGraph from "~/components/product/VariantGraph"
import { OverviewPage } from "~/components/design-system/pages"
import VariantInspector from "~/components/product/VariantInspector"

export const Route = createFileRoute("/_auth/product/$productId/_layout/overview")({
  component: RouteComponent,
})

function RouteComponent() {
  usePageTracking("Product - Overview")

  const { variants, isMonthly, startDate, endDate } = useVariantContext()
  const variantIds = useMemo(() => variants.map((v) => v.variantId), [variants])

  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const selectedChannel = useSkuStore((state) => state.channel)

  const { data: collections } = api.collection.getCollectionsForVariants.useQuery(
    {
      variantIds,
    },
    {
      enabled: variantIds.length > 0,
    },
  )
  const { data: hasCustomerData } = api.customer.hasCustomerData.useQuery(
    { variantIds },
    {
      enabled: variantIds.length > 0,
    },
  )

  return (
    <OverviewPage
      inspector={
        <VariantInspector
          detailsDefaultExpanded
          sections={[
            "image",
            "details",
            "similarVariants",
            "suggestedOrders",
            "adjustments",
            "purchaseOrders",
            "comments",
            "linkedVariants",
          ]}
        />
      }
    >
      <div className="flex flex-col gap-4">
        <VariantTableCard
          variantIds={variantIds}
          monthlyView={isMonthly}
          startDate={startDate}
          endDate={endDate}
          warehouseId={selectedWarehouseId}
          channel={selectedChannel}
          enableSizeExpansion={true}
          rows={[
            VariantsTableRow.Forecast,
            VariantsTableRow.Sales,
            VariantsTableRow.SalesIndex,
            VariantsTableRow.NetSales,
            VariantsTableRow.PreOrders,
            VariantsTableRow.Returns,
            VariantsTableRow.ForecastedReturns,
            VariantsTableRow.ClosingStock,
            VariantsTableRow.OptimalStock,
            VariantsTableRow.WeeksOnHand,
            VariantsTableRow.SuggestedOrders,
            VariantsTableRow.SuggestedIncomingStock,
            VariantsTableRow.Availability,
            VariantsTableRow.IncomingStock,
          ]}
        />

        <VariantGraph
          warehouseId={selectedWarehouseId}
          channel={selectedChannel}
          graphHeight={280}
          netSalesMode="off"
          showRawDemand={false}
        />

        <InventoryGraphCard
          variantIds={variantIds}
          warehouseId={selectedWarehouseId}
          channel={selectedChannel}
          isMonthly={isMonthly}
          startDate={startDate}
          endDate={endDate}
          graphHeight={280}
        />
        <div className="flex flex-row gap-4">
          {collections && collections.length > 0 && (
            <VariantCollectionSalesCard
              variantIds={variantIds}
              warehouseId={selectedWarehouseId}
              channel={selectedChannel}
              className="w-1/2"
            />
          )}
          {hasCustomerData && (
            <CustomerSalesCard
              variantIds={variantIds}
              warehouseId={selectedWarehouseId}
              channel={selectedChannel}
              className="w-1/2"
            />
          )}
        </div>
      </div>
    </OverviewPage>
  )
}
